import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "../../i18n";
import { useBoundStore } from "../../store";
import { useWorkspace } from "../../workspace/WorkspaceProvider";
import { findUser, User } from "../../services/User";
import {
  getProjectSharing,
  ProjectRole,
  ProjectSharing,
  ProjectVisibility,
  updateProjectSharing,
} from "../../services/Project";

const nameOf = (u: Pick<User, "firstName" | "lastName" | "email">) =>
  [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;

/** Owner: share a project with the organization or with named people. */
const ShareProjectDialog: React.FC<{
  project: { id: string; name: string } | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ project, onClose, onSaved }) => {
  const { t } = useI18n();
  const s = t.nav.share;
  const me = useBoundStore((state) => state.user);
  const { displayName } = useWorkspace();
  const [sharing, setSharing] = useState<ProjectSharing | null>(null);
  const [people, setPeople] = useState<User[]>([]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!project || !me?.organizationId) return;
    setSharing(null);
    setQuery("");
    void getProjectSharing(project.id).then((res) => {
      if (res.success && res.data) setSharing(res.data);
    });
    void findUser(me.organizationId).then((res) => {
      if (res.success && res.data) setPeople(res.data.filter((u) => u.id !== me.id));
    });
  }, [project, me?.organizationId, me?.id]);

  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const matches = useMemo(() => {
    if (!sharing) return [];
    const q = query.trim().toLowerCase();
    const added = new Set(sharing.members.map((m) => m.userId));
    return people
      .filter((p) => !added.has(p.id))
      .filter((p) => !q || nameOf(p).toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
      .slice(0, 6);
  }, [people, sharing, query]);

  const set = (patch: Partial<ProjectSharing>) => setSharing((cur) => (cur ? { ...cur, ...patch } : cur));

  const save = async () => {
    if (!project || !sharing) return;
    setSaving(true);
    try {
      const res = await updateProjectSharing(project.id, sharing);
      if (res.success) {
        toast.success(s.saved);
        onSaved();
        onClose();
      } else {
        toast.error(res.errors?.[0]?.detail || res.errors?.[0]?.title || s.failed);
      }
    } finally {
      setSaving(false);
    }
  };

  const roleSelect = (value: ProjectRole, onChange: (r: ProjectRole) => void, label: string) => (
    <Select value={value} onValueChange={(v) => onChange(v as ProjectRole)}>
      <SelectTrigger className="h-8 w-32 text-xs" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="editor">{s.canEdit}</SelectItem>
        <SelectItem value="viewer">{s.canView}</SelectItem>
      </SelectContent>
    </Select>
  );

  const option = (value: ProjectVisibility, title: string, hint: string) => (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
        sharing?.visibility === value ? "border-brand bg-brand/5" : ""
      }`}
    >
      <input
        type="radio"
        name="visibility"
        className="mt-1"
        checked={sharing?.visibility === value}
        onChange={() => set({ visibility: value })}
      />
      <span className="flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
      {value === "organization" && sharing?.visibility === "organization" &&
        roleSelect(sharing.organizationRole, (r) => set({ organizationRole: r }), s.organization(""))}
    </label>
  );

  return (
    <Dialog open={project != null} onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{project ? s.title(project.name) : ""}</DialogTitle>
          <DialogDescription>{s.chatsStayPrivate}</DialogDescription>
        </DialogHeader>
        {!sharing ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-2">
            {option("private", s.onlyMe, s.onlyMeHint)}
            {option("organization", s.organization(displayName || s.yourOrganization), s.organizationHint)}
            {option("members", s.people, s.peopleHint)}

            {sharing.visibility === "members" && (
              <div className="space-y-2 rounded-lg border p-3">
                {sharing.members.length === 0 && <p className="text-sm text-muted-foreground">{s.nobodyYet}</p>}
                {sharing.members.map((m) => {
                  const person = byId.get(m.userId);
                  return (
                    <div key={m.userId} className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm">{person ? nameOf(person) : m.userId}</div>
                        {person && nameOf(person) !== person.email && (
                          <div className="truncate text-xs text-muted-foreground">{person.email}</div>
                        )}
                      </div>
                      {roleSelect(
                        m.role,
                        (r) => set({ members: sharing.members.map((x) => (x.userId === m.userId ? { ...x, role: r } : x)) }),
                        s.canEdit,
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={s.remove}
                        onClick={() => set({ members: sharing.members.filter((x) => x.userId !== m.userId) })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
                <div className="pt-1">
                  <Input placeholder={s.searchPlaceholder} value={query} onChange={(e) => setQuery(e.target.value)} aria-label={s.addPerson} />
                  {query.trim() && (
                    <div className="mt-1 rounded-md border">
                      {matches.length === 0 ? (
                        <p className="p-2 text-sm text-muted-foreground">{s.noMatches}</p>
                      ) : (
                        matches.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              set({ members: [...sharing.members, { userId: p.id, role: "editor" }] });
                              setQuery("");
                            }}
                          >
                            {nameOf(p)} <span className="text-xs text-muted-foreground">{p.email}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {s.canEdit}: {s.canEditHint} · {s.canView}: {s.canViewHint}
                </p>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {s.cancel}
          </Button>
          <Button onClick={() => void save()} disabled={!sharing || saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {s.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareProjectDialog;
